// SCR-STAFF-953 — پیام‌های ورودی (US-MSG-040 تا 042؛ AP-MSG-01): صف پیام‌های نامعلوم (پیش‌فرض باز) با مراجعه‌کننده منطبق و متن پیامک
// (خوانده از هاب INT؛ مشاهده در Audit ثبت می‌شود)، پاسخ با متن آزاد (بررسی واژه ممنوع و طول — BR-MSG-080) و بستن؛ پاسخ‌های منطبق (۱/۲) و
// انصراف‌ها برای پیگیری. AP-CHN-02: «ثبت تیکت» از پیام نامعلوم (منبع InboundMessage — AC-CHN-041.1).
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import ReplyOutlinedIcon from '@mui/icons-material/ReplyOutlined';
import DoneOutlinedIcon from '@mui/icons-material/DoneOutlined';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { unwrap, type InboundMessageView, type InboundStatus } from '@phoenix/api-client';
import { useAccess, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  ErrorState,
  faDigits,
  FormDialog,
  JalaliDate,
  ListPage,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { WriteNotice } from '../settings/common';
import { msgKeys, SCR_STAFF_953, useTemplateLabel } from './common';

const FILTERS = ['Open', 'Matched', 'OptOut', 'Resolved'] as const;

export function InboundPage() {
  const { t } = useTranslation('msg');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('msg.inbound.manage');
  const ticketing = useWriteAccess('chn.ticket.create');
  const access = useAccess();
  const queryClient = useQueryClient();
  const templateLabel = useTemplateLabel();
  const [status, setStatus] = useState<InboundStatus>('Open');
  const [replying, setReplying] = useState<InboundMessageView | null>(null);
  const [text, setText] = useState('');
  const inbound = useQuery({
    queryKey: msgKeys.inbound(session?.tenantId, status),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/messaging/inbound-messages', { params: { query: { status, limit: 100 } } }),
      ),
  });
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['messaging', 'inbound'] });
  const reply = useMutation({
    mutationFn: ({ id, value }: { id: string; value: string }) =>
      unwrap(
        tenantApi.POST('/api/v1/messaging/inbound-messages/{id}:reply', {
          params: { path: { id } },
          body: { text: value },
        }),
      ),
    onSuccess: () => {
      setReplying(null);
      setText('');
      invalidate();
    },
  });
  const resolve = useMutation({
    mutationFn: (id: string) =>
      unwrap(tenantApi.POST('/api/v1/messaging/inbound-messages/{id}:resolve', { params: { path: { id } } })),
    onSuccess: invalidate,
  });

  const columns: DataTableColumn<InboundMessageView>[] = [
    {
      id: 'time',
      headerKey: 'msg:inbound.columns.time',
      cell: (row) => <JalaliDate value={row.receivedAt} withTime />,
    },
    {
      id: 'from',
      headerKey: 'msg:inbound.columns.from',
      cell: (row) =>
        row.patientId ? (
          <Link component={RouterLink} to={`/patients/${row.patientId}?tab=messages`}>
            {row.patientName ?? t('msg:inbound.patient')}
          </Link>
        ) : (
          <bdi dir="ltr">{row.phoneLast4 ? `…${faDigits(row.phoneLast4)}` : t('msg:inbound.unknown')}</bdi>
        ),
    },
    {
      id: 'text',
      headerKey: 'msg:inbound.columns.text',
      cell: (row) => (
        <span data-testid={`inbound-text-${row.id}`}>
          {row.text ?? t(`msg:inbound.kind.${row.kind}`, { defaultValue: row.kind })}
        </span>
      ),
    },
    {
      id: 'related',
      headerKey: 'msg:inbound.columns.related',
      cell: (row) => (row.messageTemplate ? templateLabel(row.messageTemplate) : ''),
    },
    {
      id: 'status',
      headerKey: 'msg:inbound.columns.status',
      cell: (row) => (
        <span data-testid={`inbound-status-${row.id}`} data-status={row.status}>
          {t(`msg:inbound.status.${row.status}`)}
        </span>
      ),
    },
    {
      id: 'actions',
      headerKey: 'msg:inbound.columns.actions',
      cell: (row) =>
        row.status === 'Open' ? (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title={t('msg:inbound.reply')}>
              <span>
                <IconButton
                  aria-label={t('msg:inbound.reply')}
                  onClick={() => setReplying(row)}
                  disabled={!write.canWrite}
                  data-testid={`inbound-reply-${row.id}`}
                >
                  <ReplyOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
            {ticketing.canWrite && access.featureEnabled('feature.chn.ticketing') ? (
              <Tooltip title={t('msg:inbound.create_ticket')}>
                <IconButton
                  aria-label={t('msg:inbound.create_ticket')}
                  component={RouterLink}
                  to={`/tickets?${new URLSearchParams({ new: '1', source: 'InboundMessage', ref: `msg.inbound:${row.id}`, ...(row.patientId ? { patientId: row.patientId, patientName: row.patientName ?? '' } : {}) }).toString()}`}
                  data-testid={`inbound-ticket-${row.id}`}
                >
                  <ConfirmationNumberOutlinedIcon />
                </IconButton>
              </Tooltip>
            ) : null}
            <Tooltip title={t('msg:inbound.resolve')}>
              <span>
                <IconButton
                  aria-label={t('msg:inbound.resolve')}
                  onClick={() => resolve.mutate(row.id)}
                  disabled={!write.canWrite || resolve.isPending}
                  data-testid={`inbound-resolve-${row.id}`}
                >
                  <DoneOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        ) : null,
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_953}
      titleKey="msg:inbound.title"
      subtitleKey="msg:inbound.subtitle"
      filters={
        <Stack spacing={1}>
          <TextField
            select
            size="small"
            label={t('msg:inbound.filter')}
            value={status}
            onChange={(event) => setStatus(event.target.value as InboundStatus)}
            sx={{ maxWidth: 220 }}
            slotProps={{ htmlInput: { 'data-testid': 'inbound-filter' } }}
          >
            {FILTERS.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`msg:inbound.status.${value}`)}
              </MenuItem>
            ))}
          </TextField>
          <WriteNotice write={write} />
          {resolve.error ? <ErrorState error={resolve.error} compact /> : null}
        </Stack>
      }
    >
      <DataTable
        captionKey="msg:inbound.caption"
        columns={columns}
        rows={inbound.data?.items}
        getRowId={(row) => row.id}
        isLoading={inbound.isPending}
        error={inbound.error}
        onRetry={() => void inbound.refetch()}
        emptyTitleKey="msg:inbound.empty"
      />
      {replying ? (
        <FormDialog
          open
          titleKey="msg:inbound.reply_title"
          screenId={SCR_STAFF_953}
          onSubmit={() => reply.mutate({ id: replying.id, value: text })}
          onClose={() => {
            setReplying(null);
            setText('');
            reply.reset();
          }}
          isDirty={text.length > 0}
          isSubmitting={reply.isPending}
          submitError={reply.error}
          submitKey="msg:inbound.send"
        >
          {replying.text ? (
            <TextField
              label={t('msg:inbound.original')}
              value={replying.text}
              multiline
              slotProps={{ input: { readOnly: true } }}
            />
          ) : null}
          <TextField
            label={t('msg:inbound.reply_text')}
            value={text}
            onChange={(event) => setText(event.target.value)}
            multiline
            minRows={3}
            required
            helperText={t('msg:manual.length', { count: faDigits(text.length) })}
            slotProps={{ htmlInput: { maxLength: 330, 'data-testid': 'inbound-reply-text' } }}
          />
        </FormDialog>
      ) : null}
    </ListPage>
  );
}
